import useCheckoutSession from '../../../hooks/useCheckoutSession';
import * as React from 'react';
import {useNavigate} from 'react-router-dom';
import {Box, Button, Card, CardContent, Radio, Stack, Typography} from '@mui/material';
import * as LogicService from '../../../services/LogicService';
import * as CommunicationService from '../../../services/CommunicationService';
import PriceOffer from './PriceOffer';
import {hasPaidPromotionExtras} from '../../../services/PromotionEligibility';
import TeregoPromotionInfoDialog from './TeregoPromotionInfoDialog';
import TeregoTalonUploadDialog from './TeregoTalonUploadDialog';

export default function OrderOffer({translate, checkoutBasePath = '', promotionContext = false}) {
    const session = useCheckoutSession(checkoutBasePath ? 'comenzi' : 'legacy');
    const navigate = useNavigate();
    const [busy, setBusy] = React.useState(false);
    const promotionSubmitInFlight = React.useRef(false);
    const [talonUploadEnabled, setTalonUploadEnabled] = React.useState(false);
    const [promotionInfoOpen, setPromotionInfoOpen] = React.useState(false);
    const [promotionInfoError, setPromotionInfoError] = React.useState(null);
    const [talonDialogOpen, setTalonDialogOpen] = React.useState(false);
    React.useEffect(() => {
        if (!promotionContext) {
            return undefined;
        }
        const cancelLookup = LogicService.getFeatureSwitchCallback('TEREGO_TALON_UPLOAD_ENABLED', (featureSwitch) => {
            setTalonUploadEnabled(featureSwitch?.value === true);
        });
        return cancelLookup;
    }, [promotionContext]);
    const order = session.getOrder() || {};
    const promotionFlow = promotionContext && order.promotionFlowActive === true;
    const paidExtras = hasPaidPromotionExtras(order);
    const eligible = order.promotionEligible === true && !paidExtras;
    // The server decides whether the free option may be offered, but the
    // customer must still be able to choose between the two available paths.
    // Keep the recommendation as the initial selection only; do not hard-code
    // either radio after the component has mounted.
    const [selectedChoice, setSelectedChoice] = React.useState(
        eligible ? 'TEREGO_PROMOTION' : 'STANDARD'
    );

    const back = () => {
        session.undefineMyOrder();
        navigate(`${checkoutBasePath}${translate ? '/en' : '/'}`);
    };

    const selectPromotionChoice = (choice) => {
        if ((choice === 'TEREGO_PROMOTION' && !eligible) || promotionSubmitInFlight.current) return;
        promotionSubmitInFlight.current = true;
        setSelectedChoice(choice);
        setBusy(true);
        setPromotionInfoError(null);
        const idempotencyKey = `${order.id}-${choice}-${Date.now()}`;
        CommunicationService.selectCheckout(order.id, choice, idempotencyKey, order.promotionAccessToken)
            .then((response) => {
                if (!session.isCurrent()) return;
                session.updateOrderCheckout(choice, response.promotionStatus,
                    choice === 'TEREGO_PROMOTION' ? 0 : order.quotedPrice, response.paymentCode);
                if (choice === 'TEREGO_PROMOTION' && talonUploadEnabled) {
                    // Keep the checkout at the offer step until Terego has
                    // acknowledged the uploaded talon. This prevents a reload
                    // or cancellation from advancing directly to signing.
                    session.updateOrderState('PRICE');
                    setPromotionInfoOpen(false);
                    setTalonDialogOpen(true);
                } else {
                    setPromotionInfoOpen(false);
                    navigate(`${checkoutBasePath}${translate ? '/en' : '/'}`);
                }
            })
            .catch(() => {
                if (choice === 'TEREGO_PROMOTION') {
                    setPromotionInfoError(translate ? 'The selected option is temporarily unavailable. Please try again.' : 'Opțiunea selectată nu este disponibilă momentan. Încearcă din nou.');
                } else {
                    LogicService.setAlertMessageAndSeverity({message: translate ? 'The selected option is temporarily unavailable' : 'Opțiunea selectată nu este disponibilă momentan', key: 'terego_promotion', severity: 'error'});
                }
                setBusy(false);
            })
            .finally(() => {
                promotionSubmitInFlight.current = false;
            });
    };

    if (promotionFlow) {
        const quotedPrice = order.quotedPrice === undefined || order.quotedPrice === null ? order.price : order.quotedPrice;
        const selectChoice = (choice) => {
            if (busy) {
                return;
            }
            setSelectedChoice(choice);
        };
        const openPromotionInfo = () => {
            if (busy) {
                return;
            }
            setSelectedChoice('TEREGO_PROMOTION');
            setPromotionInfoError(null);
            setPromotionInfoOpen(true);
        };
        return (
            <Box>
                <TeregoPromotionInfoDialog
                    open={promotionInfoOpen}
                    translate={translate}
                    busy={busy}
                    error={promotionInfoError}
                    onClose={() => {
                        if (!busy) {
                            setPromotionInfoOpen(false);
                            setPromotionInfoError(null);
                        }
                    }}
                    onContinue={() => selectPromotionChoice('TEREGO_PROMOTION')}
                />
                <TeregoTalonUploadDialog
                    open={talonDialogOpen}
                    order={session.getOrder() || order}
                    translate={translate}
                    onClose={() => {
                        setTalonDialogOpen(false);
                        setBusy(false);
                    }}
                    onAccepted={(response) => {
                        session.recordTeregoTalonIntake(response);
                        session.updateOrderState('SIGNATURE');
                        setTalonDialogOpen(false);
                        navigate(`${checkoutBasePath}${translate ? '/en' : '/'}`);
                    }}
                />
                <Typography variant="h6" sx={{mb: 2}}>
                    {translate ? 'Choose how you want to continue' : 'Alege cum vrei să continui'}
                </Typography>
                {paidExtras && <Typography variant="body2" sx={{mb: 2}}>
                    {translate ? 'Orders with translation or apostille are available only with payment.'
                        : 'Comenzile cu traducere sau apostilă sunt disponibile doar contra cost.'}
                </Typography>}
                {eligible && <Card
                    variant="outlined"
                    onClick={openPromotionInfo}
                    sx={{
                        mb: 2,
                        cursor: busy ? 'default' : 'pointer',
                        border: selectedChoice === 'TEREGO_PROMOTION' ? '2px solid #12a05c' : undefined,
                        backgroundColor: selectedChoice === 'TEREGO_PROMOTION' ? '#f3fff8' : undefined
                    }}
                >
                    <CardContent sx={{display: 'flex', alignItems: 'center', gap: 2}}>
                        <Radio
                            name="promotion-checkout-choice"
                            aria-label={translate ? 'Terego free option' : 'Opțiunea gratuită Terego'}
                            value="TEREGO_PROMOTION"
                            checked={selectedChoice === 'TEREGO_PROMOTION'}
                            color="success"
                            onChange={openPromotionInfo}
                            onClick={(event) => {
                                event.stopPropagation();
                                openPromotionInfo();
                            }}
                        />
                        <Box sx={{flexGrow: 1}}>
                            <Stack direction="row" alignItems="center" spacing={1.5} sx={{mb: 0.5}}>
                                <Typography variant="h3" color="success.main">GRATIS</Typography>
                                <Box
                                    component="img"
                                    src="/terego-logo.png"
                                    alt="Terego"
                                    sx={{
                                        width: {xs: 110, sm: 150},
                                        height: 'auto',
                                        maxWidth: '100%',
                                        display: 'block',
                                    }}
                                />
                            </Stack>
                            <Typography variant="subtitle1">prin partenerul nostru Terego.ro</Typography>
                            <Typography variant="body2">{translate ? 'Complete your RCA profile and receive the criminal record free.' : 'Finalizează profilul RCA și primește cazierul judiciar gratuit.'}</Typography>
                        </Box>
                        <Button
                            variant="contained"
                            color="success"
                            disabled={busy}
                            onClick={(event) => {
                                event.stopPropagation();
                                openPromotionInfo();
                            }}
                        >
                            {translate ? 'Continue free' : 'Continuă gratuit'}
                        </Button>
                    </CardContent>
                </Card>}
                <Card
                    variant="outlined"
                    onClick={() => selectChoice('STANDARD')}
                    sx={{
                        mb: 2,
                        cursor: busy ? 'default' : 'pointer',
                        border: selectedChoice === 'STANDARD' ? '2px solid #1976d2' : undefined,
                        backgroundColor: selectedChoice === 'STANDARD' ? '#f5f9ff' : undefined
                    }}
                >
                    <CardContent sx={{display: 'flex', alignItems: 'center', gap: 2}}>
                        <Radio
                            name="promotion-checkout-choice"
                            aria-label={translate ? 'Standard payment option' : 'Opțiunea de plată standard'}
                            value="STANDARD"
                            checked={selectedChoice === 'STANDARD'}
                            color="primary"
                            onChange={() => selectChoice('STANDARD')}
                            onClick={(event) => event.stopPropagation()}
                        />
                        <Box sx={{flexGrow: 1}}>
                            <Typography variant="h4">{quotedPrice} RON</Typography>
                            <Typography variant="subtitle1">{translate ? 'Standard payment' : 'Plată standard'}</Typography>
                        </Box>
                        <Button
                            variant="contained"
                            disabled={busy}
                            onClick={(event) => {
                                event.stopPropagation();
                                selectPromotionChoice('STANDARD');
                            }}
                        >
                            {translate ? 'Continue' : 'Continuă'}
                        </Button>
                    </CardContent>
                </Card>
                <Button fullWidth variant="contained" color="warning" onClick={back} disabled={busy}>
                    {translate ? 'Back' : 'Înapoi'}
                </Button>
            </Box>
        );
    }

    const toDocuments = () => {
        session.verifyTransactionAndReturn();
        navigate(`${checkoutBasePath}${translate ? '/en/complete-offer' : '/complete-offer'}`);
    };

    return (
        <Box>
            <Typography variant="subtitle2"><PriceOffer order={session.getOrder() || order} /></Typography>
            <Typography variant="h6" sx={{marginBottom: '10px'}}>
                {translate ? 'Upload the necessary documents to continue' : 'Încarcă documentele necesare pentru a continua'}
            </Typography>
            <Stack direction={{xs: 'column', sm: 'row'}} spacing={2}>
                <Button fullWidth size="large" variant="contained" color="warning" onClick={back}>
                    {translate ? 'Back' : 'Înapoi'}
                </Button>
                <Button fullWidth size="large" variant="contained" onClick={toDocuments}>
                    {translate ? 'Upload documents' : 'Încărcare documente'}
                </Button>
            </Stack>
        </Box>
    );
}
